import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { feedsApi } from "@/modules/cf-feed/presentation/api/client";
import {
  feedsKeys,
  usePatchFeedSessionProduct,
  usePutSessionProductOptions,
  useSessionProductOptionCandidatesQuery,
  useSessionProductOptionsQuery,
} from "@/modules/cf-feed/presentation/api/queries";
import {
  formatKip,
  formatPriceInput,
  maskDiscountFixedLakInput,
  maskPriceInput,
  parseDiscountFixedLakInput,
  parsePriceInput,
} from "@/modules/cf-feed/presentation/ui/table-helpers";

type DraftOption = {
  adminOptionDefinitionId: string;
  label: string;
  posDefaultSurchargeLak: number;
  surchargeLak: string;
  isAutoAttach: boolean;
  requiresCustomerInput: boolean;
  selected: boolean;
};

type SessionProductOptionsDialogProps = {
  feedId: string;
  /** 1 = แก้รายตัว · มากกว่า 1 = bulk (ต้องมี option ชุดเดียวกัน) */
  sessionProductIds: string[];
  productTitle: string;
  discountPercent: number | null;
  discountFixedLak: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

function parseOptionalPercent(raw: string): number | null | undefined {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return undefined;
  return n;
}

export function SessionProductOptionsDialog({
  feedId,
  sessionProductIds,
  productTitle,
  discountPercent,
  discountFixedLak,
  open,
  onOpenChange,
  onSaved,
}: SessionProductOptionsDialogProps) {
  const primaryId = sessionProductIds[0] ?? null;
  const isBulk = sessionProductIds.length > 1;
  const linkedQuery = useSessionProductOptionsQuery(feedId, primaryId);
  const candidatesQuery = useSessionProductOptionCandidatesQuery(
    feedId,
    primaryId,
  );
  const putOptions = usePutSessionProductOptions(feedId);
  const patchProduct = usePatchFeedSessionProduct(feedId);
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<DraftOption[]>([]);
  const [discountPctDraft, setDiscountPctDraft] = useState("");
  const [discountFixedDraft, setDiscountFixedDraft] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    if (!open || !primaryId) return;
    setDiscountPctDraft(discountPercent == null ? "" : String(discountPercent));
    setDiscountFixedDraft(
      discountFixedLak == null ? "" : formatPriceInput(discountFixedLak),
    );
  }, [open, primaryId, discountPercent, discountFixedLak]);

  useEffect(() => {
    if (!open || !primaryId) return;
    const candidates = candidatesQuery.data?.items ?? [];
    const linked = linkedQuery.data?.items ?? [];
    const linkedById = new Map(
      linked.map((row) => [row.adminOptionDefinitionId, row]),
    );

    setDrafts(
      candidates.map((c) => {
        const existing = linkedById.get(c.adminOptionDefinitionId);
        const amount = existing?.surchargeLak ?? c.posDefaultSurchargeLak;
        return {
          adminOptionDefinitionId: c.adminOptionDefinitionId,
          label: existing?.labelSnapshot ?? c.label,
          posDefaultSurchargeLak: c.posDefaultSurchargeLak,
          surchargeLak: formatPriceInput(amount),
          isAutoAttach: existing?.isAutoAttach ?? true,
          requiresCustomerInput:
            existing?.requiresCustomerInput ?? c.hasRequiredFields,
          selected: Boolean(existing) || c.alreadyLinked,
        };
      }),
    );
  }, [open, primaryId, candidatesQuery.data?.items, linkedQuery.data?.items]);

  const handleSave = async () => {
    if (!primaryId || sessionProductIds.length === 0) return;

    const parsedPct = parseOptionalPercent(discountPctDraft);
    if (parsedPct === undefined) return;
    const parsedFixed = discountFixedDraft.trim()
      ? parseDiscountFixedLakInput(discountFixedDraft)
      : null;

    const items = drafts
      .filter((d) => d.selected)
      .map((d, index) => ({
        adminOptionDefinitionId: d.adminOptionDefinitionId,
        surchargeLak: parsePriceInput(d.surchargeLak) ?? 0,
        isAutoAttach: d.isAutoAttach,
        requiresCustomerInput: d.requiresCustomerInput,
        sortOrder: index,
      }));

    const discountBody = {
      discountPercent: parsedPct,
      discountFixedLak: discountFixedDraft.trim()
        ? (parsedFixed ?? null)
        : null,
    };

    try {
      if (isBulk) {
        setBulkSaving(true);
        const result = await feedsApi.putSessionProductOptionsBulk({
          feedId,
          sessionProductIds,
          items,
          ...discountBody,
        });
        void qc.invalidateQueries({
          queryKey: [...feedsKeys.detail(feedId), "sessionProducts"],
        });
        void qc.invalidateQueries({
          queryKey: feedsKeys.postContext(feedId),
        });
        void qc.invalidateQueries({
          queryKey: ["feeds", "sessionProductOptions"],
        });
        void qc.invalidateQueries({
          queryKey: ["feeds", "sessionProductOptionCandidates"],
        });
        if (result.failed.length > 0) {
          toast.error(
            `ບັນທຶກ option ສຳເລັດ ${result.updated}/${sessionProductIds.length} ລາຍການ`,
          );
        } else {
          toast.success(`ບັນທຶກ option ${result.updated} ລາຍການແລ້ວ`);
        }
      } else {
        await Promise.all([
          putOptions.mutateAsync({ spId: primaryId, items }),
          patchProduct.mutateAsync({ spId: primaryId, ...discountBody }),
        ]);
      }
      onSaved?.();
      onOpenChange(false);
    } catch {
      if (isBulk) {
        toast.error("ບັນທຶກ option ຫຼາຍລາຍການລົ້ມເຫຼວ");
      }
    } finally {
      setBulkSaving(false);
    }
  };

  const saving = bulkSaving || putOptions.isPending || patchProduct.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isBulk ? "Option ສິນຄ້າ (ຫຼາຍລາຍການ)" : "Option ສິນຄ້າ"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">{productTitle}</p>
        {isBulk ? (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-muted-foreground text-xs">
            ຈະໃຊ້ຄ່າເຫຼົ່ານີ້ກັບທຸກລາຍການທີ່ເລືອກ ({sessionProductIds.length} ລາຍການ) —
            ເລືອກໄດ້ເທົ່ານັ້ນເມື່ອຊຸດ option ຄືກັນ
          </p>
        ) : null}

        <div className="space-y-3 rounded-md border border-border p-3">
          <div>
            <h3 className="font-medium text-sm">ສ່ວນຫຼຸດສິນຄ້ານີ້</h3>
            <p className="text-muted-foreground text-xs">
              ວ່າງ = ໃຊ້ສ່ວນຫຼຸດລາຍການໂພສ · ຖ້າຕັ້ງແລ້ວຈະ override ລາຍການ (ບໍ່ລົດ ຊື່/ໝາຍເລກ)
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cf-sp-discount-pct">ສ່ວນຫຼຸດ (%)</Label>
              <Input
                id="cf-sp-discount-pct"
                inputMode="decimal"
                placeholder="ເຊັ່ນ 10"
                value={discountPctDraft}
                onChange={(e) => setDiscountPctDraft(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-sp-discount-fixed">ສ່ວນຫຼຸດ (LAK / ຊິ້ນ)</Label>
              <Input
                id="cf-sp-discount-fixed"
                inputMode="numeric"
                className="tabular-nums"
                placeholder="5.000"
                value={discountFixedDraft}
                onChange={(e) =>
                  setDiscountFixedDraft(maskPriceInput(e.target.value))
                }
                onBlur={() =>
                  setDiscountFixedDraft(
                    maskDiscountFixedLakInput(discountFixedDraft),
                  )
                }
              />
              <p className="text-muted-foreground text-xs">ປັດເປັນຫຼັກພັນ (x,000)</p>
            </div>
          </div>
        </div>

        {candidatesQuery.isLoading || linkedQuery.isLoading ? (
          <p className="text-sm">ກຳລັງໂຫຼດ...</p>
        ) : drafts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ພົບ option ໃນ POS — ຕ້ອງມີ ຊື່ (NAME_PRINT) ຫຼື ໝາຍເລກ (NUMBER_PRINT)
            ກ່ອນ (ຮັນ seed:cf-session-pricing)
          </p>
        ) : (
          <ul className="space-y-3">
            {drafts.map((d) => (
              <li
                key={d.adminOptionDefinitionId}
                className="rounded-md border border-border p-3"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{d.label}</p>
                    <p className="text-muted-foreground text-xs">
                      ລາຄາ POS: {formatKip(d.posDefaultSurchargeLak)}
                    </p>
                  </div>
                  <Switch
                    checked={d.selected}
                    onCheckedChange={(checked) =>
                      setDrafts((prev) =>
                        prev.map((row) =>
                          row.adminOptionDefinitionId ===
                          d.adminOptionDefinitionId
                            ? { ...row, selected: checked }
                            : row,
                        ),
                      )
                    }
                  />
                </div>
                {d.selected ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label
                        htmlFor={`option-price-${d.adminOptionDefinitionId}`}
                        className="mb-1 block text-xs"
                      >
                        ລາຄາ CF (0 = ຟຣີ)
                      </Label>
                      <Input
                        id={`option-price-${d.adminOptionDefinitionId}`}
                        inputMode="numeric"
                        className="tabular-nums"
                        value={d.surchargeLak}
                        onChange={(e) =>
                          setDrafts((prev) =>
                            prev.map((row) =>
                              row.adminOptionDefinitionId ===
                              d.adminOptionDefinitionId
                                ? {
                                    ...row,
                                    surchargeLak: maskPriceInput(
                                      e.target.value,
                                    ),
                                  }
                                : row,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="flex flex-col justify-end gap-2">
                      <Label
                        htmlFor={`option-auto-${d.adminOptionDefinitionId}`}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Switch
                          id={`option-auto-${d.adminOptionDefinitionId}`}
                          checked={d.isAutoAttach}
                          onCheckedChange={(checked) =>
                            setDrafts((prev) =>
                              prev.map((row) =>
                                row.adminOptionDefinitionId ===
                                d.adminOptionDefinitionId
                                  ? { ...row, isAutoAttach: checked }
                                  : row,
                              ),
                            )
                          }
                        />
                        ຕິດອໍເດີອັດຕະໂນມັດ
                      </Label>
                      <Label
                        htmlFor={`option-input-${d.adminOptionDefinitionId}`}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Switch
                          id={`option-input-${d.adminOptionDefinitionId}`}
                          checked={d.requiresCustomerInput}
                          onCheckedChange={(checked) =>
                            setDrafts((prev) =>
                              prev.map((row) =>
                                row.adminOptionDefinitionId ===
                                d.adminOptionDefinitionId
                                  ? {
                                      ...row,
                                      requiresCustomerInput: checked,
                                    }
                                  : row,
                              ),
                            )
                          }
                        />
                        ຕ້ອງໃຫ້ລູກຄ້າກອກ
                      </Label>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={!primaryId || saving}
          >
            {saving
              ? "ກຳລັງບັນທຶກ..."
              : isBulk
                ? `ບັນທຶກ ${sessionProductIds.length} ລາຍການ`
                : "ບັນທຶກ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
